import posthog from "posthog-js"

import type { User } from "@/types"

type RecordingStartMode = "on_load" | "after_login"

const POSTHOG_DEFAULT_HOST = "https://us.i.posthog.com"

const getEnvString = (value: unknown, fallback = ""): string => {
  if (typeof value === "string") return value
  return fallback
}

const getExcludedEmailDomains = (): string[] => {
  const raw = getEnvString(
    import.meta.env.POSTHOG_REPLAY_EXCLUDED_EMAIL_DOMAINS,
    ""
  )
  if (!raw.trim()) return []
  return raw
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
}

const shouldRecordReplayForEmail = (email?: string): boolean => {
  if (!email) return true

  const excludedDomains = getExcludedEmailDomains()
  if (excludedDomains.length === 0) return true

  const normalized = email.trim().toLowerCase()
  return !excludedDomains.some((domain) => normalized.endsWith(domain))
}

const getRecordingStartMode = (): RecordingStartMode => {
  const mode = getEnvString(import.meta.env.POSTHOG_RECORDING_START, "on_load")
  return mode === "after_login" ? "after_login" : "on_load"
}

let initialized = false

export const initPosthog = (): void => {
  if (initialized) return

  const key = getEnvString(import.meta.env.POSTHOG_API_KEY, "").trim()
  if (!key) {
    return
  }

  const host = getEnvString(
    import.meta.env.POSTHOG_HOST,
    POSTHOG_DEFAULT_HOST
  ).trim()

  posthog.init(key, {
    api_host: host || POSTHOG_DEFAULT_HOST,
    // Keep parity with our channel client template: modern defaults enable sane behavior
    // (including error autocapture when enabled in PostHog project settings).
    defaults: "2025-05-24",
    // Session Replay (Screen Recording)
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
    },
  })

  initialized = true

  // Optional: if we want to move to "only after login" later, this keeps it a config change.
  // - on_load: start recording immediately (current spec)
  // - after_login: stop recording until we identify a user
  if (getRecordingStartMode() === "after_login") {
    posthog.stopSessionRecording?.()
  }
}

export const syncPosthogUser = (user: User | null): void => {
  if (!initialized) return

  if (!user) {
    posthog.reset()
    // Make the semantics of POSTHOG_RECORDING_START explicit:
    // - on_load: keep recording (anonymous) after logout
    // - after_login: stop recording after logout until the next identify()
    if (getRecordingStartMode() === "after_login") {
      posthog.stopSessionRecording?.()
    }
    return
  }

  // Use email as distinct_id to simplify dashboard usage and filtering.
  posthog.identify(user.email, {
    email: user.email,
    name: user.name,
  })

  // Centralized control point for internal-user replay exclusions (disabled by default).
  if (shouldRecordReplayForEmail(user.email)) {
    if (getRecordingStartMode() === "after_login") {
      posthog.startSessionRecording?.()
    }
  } else {
    posthog.stopSessionRecording?.()
  }
}
